


import { useEffect, useState } from 'react'
import Loader from '../../components/loader';
import Layout from '../../components/Layout';
import { errorMessage } from '../../utils/utils';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { IoIosWarning } from "react-icons/io";
import { toast } from 'react-toastify';
import { IoSend } from 'react-icons/io5';
import Spinner from '../../components/spinner';
import api from '../../utils/api';
import { FaArrowRightLong } from 'react-icons/fa6';
import moment from 'moment';
import { CiCalendarDate } from 'react-icons/ci';
import { MdLabelOutline } from 'react-icons/md';
import { GrAnnounce } from "react-icons/gr";
import { PiLinkFill } from 'react-icons/pi';
import { IoIosListBox } from 'react-icons/io';
import { EmptyState, ErrorState, Loading, Page } from '../../components/ui';
import { IoWallet } from 'react-icons/io5';
import { peso } from '../../components/Credits';

/** A post shows the NEW badge for this many days after it is published. */
const NEWS_NEW_DAYS = 14;

function isNewPost(dateCreated: any) {
  const posted = new Date(dateCreated).getTime();
  return !isNaN(posted) && Date.now() - posted < NEWS_NEW_DAYS * 24 * 60 * 60 * 1000;
}

export default function Dashboard() {

  const [pageLoading, setPageLoading] = useState(true);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<any>();

  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [dialogStatus, setDialogStatus] = useState('');

  const [news, setNews] = useState<any[]>([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [newsError, setNewsError] = useState('');

  const [verifyLoading, setVerifyLoading] = useState(false);

  useEffect(() => {
    const token = window.localStorage.token;

    if (!token) {
      window.location.replace('/signin');
      return;
    }

    setName(window.localStorage.name ?? window.localStorage.email ?? 'user');

    setPageLoading(false);

    getUser();
    getNews();

  }, []);


  async function verifyEmail() {

    setVerifyLoading(true);

    try {
      const res = await api.post(`/auth/email/verify`);
      const json = await res.json();
      if (res.ok) {
        setOpenDialog(false);
        toast.success(json.message);
      } else {
        toast.error(errorMessage(json));
      }
    } catch (error) {
      toast.error(errorMessage(error));
    }

    setVerifyLoading(false);
  }

  async function getUser() {
    try {
      const res = await api.get(`/user`);
      const json = await res.json();
      if (res.ok) {
        setUser(json);
        if (json.email_verified !== true) {
          setDialogStatus('warning');
          setDialogMessage(`
            <b>Your email address has not been verified yet</b><br /><br />To activate your account, please check your inbox and follow the verification link we sent you. If you don't see the email, check your spam folder or click <b>Send Email Verification</b> again.
            `);
          setOpenDialog(true);
        }
      } else {
        setError(errorMessage(json));
      }

    } catch (err) {
      setError(errorMessage(err));
    }

    setLoading(false);
  }

  async function getNews() {
    try {

      const res = await api.get('/apps/news?type=remote');
      const json = await res.json();
      if (res.ok) {
        setNews(json);
      } else {
        setNewsError(errorMessage(json));
      }

    } catch (err) {
      setNewsError(errorMessage(error));
    }

    setNewsLoading(false);
  }


  if (pageLoading) {
    return (<div className='w-screen h-screen text-center flex flex-col bg-white justify-center'><Loader /></div>)
  }

  return (
    <>
      <Layout nav='dashboard' title='Dashboard'>

            <Page>

              <p className="eyebrow">Overview</p>
              <h1 className="mt-2 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
                Hello, {name}
              </h1>
              <p className="mt-1 text-sm text-slate-500">Here is what is happening with your remote accounts.</p>

              {loading && <Loading label='Loading your account...' />}
              <ErrorState error={loading ? '' : error} />

              <div className={`mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 ${(loading || error || !user) && 'hidden'}`}>

                <a href="/remote" className='group flex flex-row items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:border-emerald-300 hover:shadow-md'>
                  <span className='flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600'><PiLinkFill size={20} /></span>
                  <span className='grow min-w-0'>
                    <span className='block font-bold text-slate-900'>Remote Accounts</span>
                    <span className='block text-xs text-slate-500'>Manage your VPN accounts</span>
                  </span>
                  <FaArrowRightLong className='shrink-0 text-slate-300 transition-all group-hover:translate-x-1 group-hover:text-emerald-600' />
                </a>

                <a href="/credits" className='group flex flex-row items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:border-emerald-300 hover:shadow-md'>
                  <span className='flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600'><IoWallet size={20} /></span>
                  <span className='grow min-w-0'>
                    <span className='block font-bold text-slate-900'>Credits</span>
                    <span className='block text-xs text-slate-500'>{peso(user?.credits)} available · Top up</span>
                  </span>
                  <FaArrowRightLong className='shrink-0 text-slate-300 transition-all group-hover:translate-x-1 group-hover:text-emerald-600' />
                </a>

                <a href="/transaction" className='group flex flex-row items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:border-emerald-300 hover:shadow-md'>
                  <span className='flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600'><IoIosListBox size={20} /></span>
                  <span className='grow min-w-0'>
                    <span className='block font-bold text-slate-900'>Transactions</span>
                    <span className='block text-xs text-slate-500'>View your purchase history</span>
                  </span>
                  <FaArrowRightLong className='shrink-0 text-slate-300 transition-all group-hover:translate-x-1 group-hover:text-emerald-600' />
                </a>

              </div>

              <h2 className='mt-9 mb-4 text-sm font-bold uppercase tracking-wider text-slate-400'>News &amp; Updates</h2>

              {newsLoading && <Loading label='Loading updates...' />}
              <ErrorState error={newsLoading ? '' : newsError} />

              {(!newsLoading && !newsError && news?.length === 0) &&
                <EmptyState icon={GrAnnounce} title='No updates yet' message='Announcements from the KPTD team will appear here.' />}

              <ul className='flex flex-col gap-4 pb-6'>{news?.map((item: any, index: number) => {

                const date = item.date_created ? moment(item.date_created).format('MMMM D, YYYY [at] h:mm a') : '';
                const isNew = isNewPost(item.date_created);

                return <li key={`${index}-news`} className={`flex flex-col overflow-hidden rounded-2xl border bg-white shadow-sm transition hover:shadow-md ${isNew ? 'border-emerald-300 ring-2 ring-emerald-100' : 'border-slate-200'}`}>
                  <div className='flex flex-row items-start gap-3 p-4 sm:p-5'>
                    <span className='mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600'>
                      <MdLabelOutline size={18} />
                    </span>
                    <div className='grow min-w-0'>
                      <div className='flex flex-row flex-wrap items-center gap-x-2 gap-y-1'>
                        {isNew && <span className='shrink-0 rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white shadow-sm shadow-emerald-600/30'>New</span>}
                        <div className='min-w-0 font-bold text-slate-900 break-words' dangerouslySetInnerHTML={{ __html: item.title }} />
                      </div>
                      <div className='mt-0.5 flex flex-row items-center gap-1.5 text-xs text-slate-400'><CiCalendarDate />{date}</div>
                      <div className='mt-2.5 text-sm leading-relaxed text-slate-600 break-words' dangerouslySetInnerHTML={{ __html: item.message }} />
                    </div>
                  </div>
                  <div className='flex flex-row items-center gap-2 border-t border-slate-100 bg-slate-50/60 px-4 py-2.5 sm:px-5'>
                    <div className='text-[11px] text-slate-400'>Posted by</div>
                    <div className='text-[11px] font-bold text-slate-600'>{item.created_by}</div>
                  </div>
                </li>
              })}</ul>

            </Page>

          </Layout>


      <Dialog open={openDialog} onClose={() => { }} className="relative z-[60]">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />

        <div className="fixed inset-0 z-[60] w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
            >
              <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">

                  {dialogStatus === 'ok' && <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-green-200 sm:mx-0 sm:h-10 sm:w-10">
                    <CheckIcon aria-hidden="true" className="h-6 w-6 text-green-700" />
                  </div>}

                  {dialogStatus === 'error' && <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-200 sm:mx-0 sm:h-10 sm:w-10">
                    <CheckIcon aria-hidden="true" className="h-6 w-6 text-green-700" />
                  </div>}

                  {dialogStatus === 'warning' && <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-orange-200 sm:mx-0 sm:h-10 sm:w-10">
                    <IoIosWarning aria-hidden="true" className="h-6 w-6 text-orange-700" />
                  </div>}

                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">

                    {/* <DialogTitle as="h3" className="text-base font-semibold leading-6 text-gray-900">Demo Sent</DialogTitle> */}

                    <div className="mt-2">
                      <div className="text-sm text-gray-700"><div dangerouslySetInnerHTML={{ __html: dialogMessage }} /></div>
                    </div>

                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 flex flex-row gap-3">
                <div className='grow'></div>
                {user?.email_verified !== true && <button className='disabled:bg-gray-500 flex flex-row items-center bg-blue-600 px-3 py-1 text-sm font-medium rounded-md text-white shadow-sm hover:bg-blue-500 active:bg-blue-700' disabled={verifyLoading} onClick={verifyEmail}>
                  {verifyLoading ? <Spinner className='inline mr-2' /> : <IoSend className='inline mr-2' />}Send Email Verification
                </button>}
                <button
                  type="button"
                  data-autofocus
                  disabled={verifyLoading}
                  onClick={() => setOpenDialog(false)}
                  className="inline-flex justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>



    </>
  )

}